import { useGameStore } from '@/store/gameStore'
import { GAME_CONFIG } from '@/config/gameConfig'
import { EventBus, GameEvents } from '../utils/EventBus'
import { MEMBERSHIP_TIERS, calculateMembershipIncome } from '@/config/membershipConfig'
import { getFacilityLevel } from '@/config/facilityRegistry'
import { LOAN_OPTIONS, SPONSORSHIP_OFFERS } from '@/config/sponsorshipConfig'
import { SEASONAL_EVENTS } from '@/config/seasonalConfig'
import { ACADEMY_COURSES } from '@/config/membershipConfig'
import { calculateAllDemands } from './DemandSystem'
import {
  collectModifiers, productMults, sumAdds, getClubSatisfactionBonus,
} from './ModifierSystem'
import { SERVICES } from '@/config/servicesConfig'
import { getDifficultyMods } from '@/config/difficultyConfig'
import { getRegionMods } from '@/config/regionConfig'
import { getDivisionOrNone, getDivisionSponsorMult } from '@/config/federationConfig'

export class EconomySystem {
  private boundHandlers: { event: string; handler: () => void }[] = []

  constructor() {
    this.setupListeners()
  }

  private listen(event: string, handler: () => void) {
    EventBus.on(event, handler)
    this.boundHandlers.push({ event, handler })
  }

  private setupListeners() {
    this.listen(GameEvents.DAY_CHANGED, () => {
      this.processDailyIncome()
      this.growCourseEnrollments()
      useGameStore.getState().checkLevelUp()
    })
    this.listen(GameEvents.WEEK_CHANGED, () => {
      this.processWeeklyExpenses()
      this.processStaffSalaries()
      this.processPlayerSalaries()
      this.growMemberships()
      this.processWeeklyCourseIncome()
    })
    this.listen(GameEvents.MONTH_CHANGED, () => {
      this.processMembershipIncome()
      this.processLoanPayments()
      this.processSponsorshipPayments()
    })
  }

  destroy() {
    for (const { event, handler } of this.boundHandlers) {
      EventBus.off(event, handler)
    }
    this.boundHandlers = []
  }

  /** Get combined seasonal effects for current day */
  private getSeasonalEffects() {
    const store = useGameStore.getState()
    // Map game month (1-based, 28 days each) to calendar month (1-12 cyclically)
    const calendarMonth = ((store.month - 1) % 12) + 1

    let customerMultiplier = 1
    let expenseModifier = 1
    let reputationBoost = 0

    for (const event of SEASONAL_EVENTS) {
      // Check if current calendar month falls within event range
      const eventEndMonth = event.startMonth + Math.ceil(event.duration / 30)
      const isActive = calendarMonth >= event.startMonth && calendarMonth < eventEndMonth

      if (isActive) {
        for (const effect of event.effects) {
          if (effect.type === 'customer_multiplier') customerMultiplier *= effect.value
          if (effect.type === 'expense_modifier') expenseModifier *= effect.value
          if (effect.type === 'reputation_boost') reputationBoost += effect.value
        }
      }
    }

    return { customerMultiplier, expenseModifier, reputationBoost }
  }

  /** Daily income from all enabled services */
  private processDailyIncome() {
    const store = useGameStore.getState()
    const courts = store.courts

    if (courts.length === 0) return

    // Clean expired effects
    store.cleanExpiredEffects()

    // Calculate income from enabled services via DemandSystem
    const demands = calculateAllDemands()
    let totalIncome = 0
    const mods = collectModifiers()
    const incomeModifier = productMults(mods, 'income.total')

    for (const demand of demands) {
      if (demand.estimatedDailyIncome <= 0) continue

      const service = SERVICES.find(s => s.id === demand.serviceId)
      let serviceIncome = demand.estimatedDailyIncome

      // Apply court upgrade multipliers for court-based services, averaged
      // across open courts (a closed court under works contributes nothing).
      if (service && (service.category === 'court' || service.category === 'class')) {
        let avgUpgradeMultiplier = 1
        const operationalCourts = courts.filter(c => c.isOperational !== false)
        if (operationalCourts.length > 0) {
          const totalMultiplier = operationalCourts.reduce(
            (sum, c) => sum + productMults(mods, 'income.court_rental', c.id), 0,
          )
          avgUpgradeMultiplier = totalMultiplier / operationalCourts.length
        }
        serviceIncome *= avgUpgradeMultiplier
      }

      // Apply active effects income modifier + difficulty + region
      serviceIncome *= incomeModifier * getDifficultyMods().incomeMultiplier * getRegionMods().incomeMultiplier

      totalIncome += serviceIncome
      store.addMoney(Math.round(serviceIncome), `${service?.emoji ?? ''} ${service?.name ?? demand.serviceId}`)
    }

    if (totalIncome > 0) {
      EventBus.emit(GameEvents.MONEY_CHANGED, { amount: totalIncome, type: 'income' })
    }

    // Seasonal reputation boost
    const seasonal = this.getSeasonalEffects()
    if (seasonal.reputationBoost > 0) {
      store.addReputation(seasonal.reputationBoost)
    }

    // Customer reviews. Facility/upgrade satisfaction now moves the roll:
    // +200 satisfaction points would be +100% chance, so a few upgrades add
    // a handful of percentage points of happy reviews.
    if (courts.length > 0) {
      const satisfactionBonus = getClubSatisfactionBonus()
      const numReviews = Math.floor(Math.random() * 3) + 1
      for (let i = 0; i < numReviews; i++) {
        const avgCondition = courts.reduce((acc, c) => acc + c.condition, 0) / courts.length
        const chance = Math.min(0.98, (avgCondition / 100) * 0.8 + 0.1 + satisfactionBonus / 200)
        const satisfied = Math.random() < chance
        store.updateReputation(satisfied)
      }
    }
  }

  /** Court maintenance costs */
  private processWeeklyExpenses() {
    const store = useGameStore.getState()
    const courts = store.courts

    if (courts.length === 0) return

    const seasonal = this.getSeasonalEffects()

    const expenseMult = getDifficultyMods().expenseMultiplier * getRegionMods().expenseMultiplier
    const mods = collectModifiers()
    let totalMaintenance = 0
    courts.forEach((court) => {
      const baseCost = court.type === 'outdoor'
        ? GAME_CONFIG.COURT_MAINTENANCE_WEEKLY
        : GAME_CONFIG.COURT_INDOOR_MAINTENANCE_WEEKLY
      totalMaintenance += baseCost * seasonal.expenseModifier * expenseMult

      const conditionDecay = productMults(mods, 'court.condition_decay', court.id)
      useGameStore.getState().updateCourt(court.id, {
        condition: Math.max(0, court.condition - 2 * conditionDecay),
      })
    })

    // Building upkeep, per registry level (legacy levels keep the old flat cost)
    for (const b of store.buildings) {
      const level = getFacilityLevel(b.type, b.level ?? 1)
      if (level) totalMaintenance += level.maintenancePerWeek * seasonal.expenseModifier * expenseMult
    }

    // Solar panels & friends: multiplier on the whole upkeep bill (1 = identity)
    totalMaintenance *= productMults(mods, 'expenses.maintenance')

    if (totalMaintenance > 0) {
      store.subtractMoney(Math.round(totalMaintenance), `Mantenimiento semana ${store.week}`)
    }

    // Press room & prestige facilities: weekly reputation drip
    const weeklyReputation = sumAdds(mods, 'reputation.weekly')
    if (weeklyReputation > 0) {
      store.addReputation(weeklyReputation)
    }
  }

  /** Staff salary payments */
  private processStaffSalaries() {
    const store = useGameStore.getState()
    const expenseMult = getDifficultyMods().expenseMultiplier * getRegionMods().expenseMultiplier
    const totalSalary = Math.round(store.staff.reduce((acc, s) => acc + s.salary, 0) * expenseMult)

    if (totalSalary > 0) {
      store.subtractMoney(totalSalary, `Salarios semana ${store.week}`)
    }
  }

  /** Player roster salary payments */
  private processPlayerSalaries() {
    const store = useGameStore.getState()
    const expenseMult = getDifficultyMods().expenseMultiplier * getRegionMods().expenseMultiplier
    const totalSalary = Math.round(store.players.reduce((acc, p) => acc + p.salary, 0) * expenseMult)

    if (totalSalary > 0) {
      store.subtractMoney(totalSalary, `Salarios de jugadores semana ${store.week}`)
    }
  }

  /** Grow membership counts for enabled tiers */
  private growMemberships() {
    const store = useGameStore.getState()
    const { enabledMembershipTiers, memberCounts } = store

    if (enabledMembershipTiers.length === 0) return

    const updated = { ...memberCounts }
    for (const tierId of enabledMembershipTiers) {
      const tier = MEMBERSHIP_TIERS.find(t => t.id === tierId)
      if (tier) {
        const current = updated[tierId] || 0
        if (current < tier.maxMembers) {
          updated[tierId] = Math.min(current + Math.floor(Math.random() * 3) + 1, tier.maxMembers)
        }
      }
    }
    store.setMemberCounts(updated)
  }

  /** Monthly membership fee income */
  private processMembershipIncome() {
    const store = useGameStore.getState()
    const income = calculateMembershipIncome(store.memberCounts)

    if (income > 0) {
      store.addMoney(income, 'Cuotas de socios')
    }
  }

  /** Monthly loan repayments */
  private processLoanPayments() {
    const store = useGameStore.getState()
    const { activeLoans } = store

    if (activeLoans.length === 0) return

    const updated = activeLoans
      .map(active => {
        const loan = LOAN_OPTIONS.find(l => l.id === active.loanId)
        if (!loan) return null

        store.subtractMoney(loan.monthlyPayment, `Prestamo: ${loan.name}`)

        const remaining = active.remainingMonths - 1
        if (remaining <= 0) return null

        return {
          ...active,
          remainingMonths: remaining,
          totalPaid: active.totalPaid + loan.monthlyPayment,
        }
      })
      .filter((l): l is NonNullable<typeof l> => l !== null)

    store.setActiveLoans(updated)
  }

  /** Monthly sponsorship income and bonuses */
  private processSponsorshipPayments() {
    const store = useGameStore.getState()
    const { activeSponsorships, month } = store

    if (activeSponsorships.length === 0) return

    // Sponsors pay more the higher the club competes (identity ≤ Cuarta)
    const divisionMult = getDivisionSponsorMult(getDivisionOrNone(store.league))

    activeSponsorships.forEach(active => {
      const sponsor = SPONSORSHIP_OFFERS.find(s => s.id === active.sponsorshipId)
      if (!sponsor) return

      store.addMoney(Math.round(sponsor.monthlyPayment * divisionMult), `Patrocinio: ${sponsor.name}`)
      sponsor.bonuses.forEach(bonus => {
        if (bonus.type === 'reputation') store.addReputation(bonus.value)
      })
    })

    // Remove expired sponsorships
    const remaining = activeSponsorships.filter(a => a.endMonth > month)
    if (remaining.length !== activeSponsorships.length) {
      store.setActiveSponsorships(remaining)
    }
  }

  /** Grow course enrollments daily (migrated from AcademyPanel useEffect) */
  private growCourseEnrollments() {
    const store = useGameStore.getState()
    const { activeCourses, courseEnrollments } = store

    if (activeCourses.length === 0) return

    const updated = { ...courseEnrollments }
    for (const courseId of activeCourses) {
      const course = ACADEMY_COURSES.find(c => c.id === courseId)
      if (!course) continue
      const current = updated[courseId] || 0
      if (current < course.groupSize && Math.random() < 0.3) {
        updated[courseId] = current + 1
      }
    }
    store.setCourseEnrollments(updated)
  }

  /** Weekly income from active academy courses */
  private processWeeklyCourseIncome() {
    const store = useGameStore.getState()
    const { activeCourses, courseEnrollments } = store

    if (activeCourses.length === 0) return

    let income = 0
    for (const courseId of activeCourses) {
      const course = ACADEMY_COURSES.find(c => c.id === courseId)
      if (!course) continue
      const students = courseEnrollments[courseId] || 0
      income += course.pricePerSession * students * 5 // 5 sessions/week
    }

    if (income > 0) {
      store.addMoney(income, 'Academia de Padel')
    }
  }

  /** Calculate projected income for a period */
  calculateProjectedIncome(days: number = 7): number {
    const demands = calculateAllDemands()
    const dailyIncome = demands.reduce((sum, d) => sum + d.estimatedDailyIncome, 0)
    return Math.round(dailyIncome * days)
  }

  /** Calculate projected expenses for a period */
  calculateProjectedExpenses(days: number = 7): number {
    const store = useGameStore.getState()
    const courts = store.courts

    const weeksInPeriod = days / 7
    let totalExpenses = 0

    courts.forEach((court) => {
      const maintenanceCost = court.type === 'outdoor'
        ? GAME_CONFIG.COURT_MAINTENANCE_WEEKLY
        : GAME_CONFIG.COURT_INDOOR_MAINTENANCE_WEEKLY
      totalExpenses += maintenanceCost * weeksInPeriod
    })

    totalExpenses += store.staff.reduce((acc, s) => acc + s.salary, 0) * weeksInPeriod

    return totalExpenses
  }
}
